import { Component, OnInit } from '@angular/core'
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms'
import { MatCalendarCellCssClasses } from '@angular/material/datepicker'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import * as moment from 'moment/moment'
import { Employee } from '../../../../../api/models/employee.model'
import { ImportCriteria } from '../../../../../api/models/import.model'
import { TimesheetProject } from '../../../../../api/models/timesheet-project.model'
import { EmployeeService } from '../../../../../api/services/employee.service'
import { ImportService } from '../../../../../api/services/import.service'
import { TimesheetProjectService } from '../../../../../api/services/timesheet-project.service'
import { SnackbarService } from '../../../../common/services/snackbar.service'
import { Utils } from '../../../../common/utils/utils'

@Component({
    selector: 'import-dialog',
    templateUrl: './import-dialog.component.html',
    styleUrls: ['./import-dialog.component.scss']
})
export class ImportDialogComponent implements OnInit {
    trackBy = Utils.trackByIndex
    dialogTitle!: string
    selectedDates: Array<moment.Moment> = new Array<moment.Moment>()
    startAt!: moment.Moment

    importFormGroup: UntypedFormGroup = new FormGroup({
        start: new FormControl<Date | null>(null),
        end: new FormControl<Date | null>(null),
        employee: new FormControl<string | null>(''),
        clientProject: new FormControl<string | null>(''),
        edited: new FormControl<boolean>(false),
        deleted: new FormControl<boolean>(false)
    })

    filteredTimesheetProjects!: Array<TimesheetProject>
    timesheetProjects!: Array<TimesheetProject>

    filteredEmployees!: Array<Employee>
    employees!: Array<Employee>

    private readonly prefix = 'ImportDialogComponent'

    constructor(
        readonly dialogRef: MatDialogRef<ImportDialogComponent>,
        readonly dialog: MatDialog,
        private readonly employeeService: EmployeeService,
        private readonly importService: ImportService,
        private readonly timesheetProjectService: TimesheetProjectService,
        private readonly snackbarService: SnackbarService
    ) {}

    async ngOnInit(): Promise<void> {
        this.dialogTitle = 'Jira backfill'

        await this.getFilteredTimesheetProjects()
        await this.getEmployees()

        this.importFormGroup.get('clientProject')?.valueChanges.subscribe((value) => this.clientQuerySearch(value))
        this.importFormGroup.get('employee')?.valueChanges.subscribe((value) => this.employeeQuerySearch(value))
    }

    cancelDialog(): void {
        this.dialogRef.close()
    }

    saveImport(): void {
        const importValues = this.importFormGroup.getRawValue()
        const start = importValues?.start || null
        const end = importValues?.end || null

        const employee =
            this.isEmployeeSelected() && this.filteredEmployees.length === 1
                ? this.filteredEmployees[0]?.id || null
                : null

        const project =
            this.isClientSelected() && this.filteredTimesheetProjects.length === 1
                ? this.filteredTimesheetProjects[0]?.id || null
                : null

        const criteria: ImportCriteria = {
            edited: this.importFormGroup.get('edited')?.value || false,
            deleted: this.importFormGroup.get('deleted')?.value || false,
            startDate: start,
            endDate: end,
            employeeId: employee,
            projectId: project
        }

        this.dialog.closeAll()
        this.importService
            .triggerJiraBackfill(criteria)
            .then(() => this.snackbarService.promtSnackBar('Jira backfill started.', 'OK', 5000))
            .catch((err: any) => this.snackbarService.handleError(this.prefix, err, true))
    }
    clientQuerySearch($event: string): void {
        const input = $event
        this.filteredTimesheetProjects = this.timesheetProjects.filter(
            (project: TimesheetProject) =>
                project.name.toLowerCase().includes(input.toLowerCase()) ||
                project.clientName.toLowerCase().includes(input.toLowerCase()) ||
                input === this.getProjectName(project)
        )
    }

    employeeQuerySearch($event: string): void {
        const input = $event
        this.filteredEmployees = this.employees.filter(
            (employee: Employee) =>
                employee.firstName?.toLowerCase().includes(input.toLowerCase()) ||
                employee.lastName?.toLowerCase().includes(input.toLowerCase()) ||
                input === this.getEmployeeName(employee)
        )
    }

    getProjectName(timesheetProject: TimesheetProject): string {
        return timesheetProject.clientName && timesheetProject.name
            ? `${timesheetProject.clientName} - ${timesheetProject.name}`
            : ''
    }

    getEmployeeName(employee: Employee): string {
        return employee.fullName ? `${employee.fullName}` : ''
    }

    isClientSelected(): boolean {
        const importCriteria = this.importFormGroup.getRawValue()
        const client = importCriteria.clientProject

        return client !== ''
    }

    clearClient(): void {
        this.importFormGroup.get('clientProject')?.setValue('')
    }

    dateClass(): (date: moment.Moment) => MatCalendarCellCssClasses {
        return (date: moment.Moment): MatCalendarCellCssClasses => {
            const highlightDate = this.selectedDates.some(
                (d) => d.date() === date.date() && d.month() === date.month() && d.year() === date.year()
            )
            const isWeekend = date.weekday() === 0 || date.weekday() === 6

            return highlightDate ? 'selected' : isWeekend ? 'weekend' : ''
        }
    }

    clearEmployee(): void {
        this.importFormGroup.get('employee')?.setValue('')
    }

    isEmployeeSelected(): boolean {
        const importCriteria = this.importFormGroup.getRawValue()
        const employee = importCriteria.employee

        return employee !== ''
    }

    async getFilteredTimesheetProjects(): Promise<void> {
        const paginatedListTimesheetProjects = await this.timesheetProjectService.getAllTimesheetProjects()
        this.filteredTimesheetProjects = paginatedListTimesheetProjects.items.filter(
            (p) => !p.name.startsWith('[ARCHIVED')
        )
        this.timesheetProjects = this.filteredTimesheetProjects
    }

    async getEmployees(): Promise<void> {
        const paginatedEmployees = await this.employeeService.getAllEmployees()
        this.filteredEmployees = paginatedEmployees.items
        this.employees = this.filteredEmployees
    }

    isValid(): boolean {
        return (
            this.importFormGroup.valid &&
            this.isDateSelected() &&
            this.importFormGroup.get('start')?.value != null &&
            !(this.importFormGroup.get('employee')?.value !== '' && this.filteredEmployees?.length === 0) &&
            !(this.importFormGroup.get('clientProject')?.value !== '' && this.filteredTimesheetProjects?.length === 0)
        )
    }

    isDateSelected(): boolean {
        const importCriteria = this.importFormGroup.getRawValue()
        const start = importCriteria.start
        const end = importCriteria.end

        return start !== null || end !== null
    }

    clearDates(): void {
        this.importFormGroup.get('start')?.setValue(null)
        this.importFormGroup.get('end')?.setValue(null)
    }
}
